import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertBannerProvider as DSAlertBannerProvider,
  StackingAlertBanner,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { ALERT_BANNER_TYPES } from '../../constants';

import { useAgentState } from './AgentContext';

import './AlertBannerContext.css';

export type AlertBannerColorTheme =
  (typeof ALERT_BANNER_TYPES)[keyof typeof ALERT_BANNER_TYPES];

export interface ShowAlertBannerOptions {
  colorTheme: AlertBannerColorTheme;
  title: string;
  description?: string;
  shouldAutoDismiss?: boolean;
  autoDismissDelay?: number;
  isFloating?: boolean;
  isColonVisible?: boolean;
  onCancel?: () => void;
}

interface AlertBannerEntry extends ShowAlertBannerOptions {
  id: string;
}

interface AlertBannerContextValue {
  show: (options: ShowAlertBannerOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const AlertBannerContext = createContext<AlertBannerContextValue | undefined>(
  undefined
);

/**
 * App-level provider for stacking alert banners. Wraps the design system
 * `AlertBannerProvider` and renders any banner queued via `useAlertBanner()`
 * as a `StackingAlertBanner`, which the DS provider hoists into a single
 * shared page-level stack.
 *
 * The provider's width is adjusted via the `--isMenuPinned` className modifier
 * so the banner stack reflows when the host sidebar collapses or expands.
 */
export function AlertBannerProvider({ children }: { children: ReactNode }) {
  const { isMenuPinned } = useAgentState();
  const [banners, setBanners] = useState<AlertBannerEntry[]>([]);
  const idCounter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setBanners((prev) => {
      const target = prev.find((b) => b.id === id);
      target?.onCancel?.();
      return prev.filter((b) => b.id !== id);
    });
  }, []);

  const dismissAll = useCallback(() => {
    setBanners((prev) => {
      prev.forEach((b) => b.onCancel?.());
      return [];
    });
  }, []);

  const show = useCallback((options: ShowAlertBannerOptions) => {
    idCounter.current += 1;
    const id = `agent-alert-banner-${idCounter.current}`;
    setBanners((prev) => [...prev, { ...options, id }]);
    return id;
  }, []);

  const value = useMemo(
    () => ({ show, dismiss, dismissAll }),
    [show, dismiss, dismissAll]
  );

  return (
    <AlertBannerContext.Provider value={value}>
      <DSAlertBannerProvider
        className={cx(
          'Agent-AlertBannerProvider',
          isMenuPinned && 'Agent-AlertBannerProvider--isMenuPinned'
        )}>
        {children}
        {banners.map((banner) => (
          <StackingAlertBanner
            key={banner.id}
            colorTheme={banner.colorTheme}
            title={banner.title}
            description={banner.description ?? ''}
            shouldAutoDismiss={banner.shouldAutoDismiss ?? true}
            autoDismissDelay={banner.autoDismissDelay}
            isFloating={banner.isFloating}
            isColonVisible={banner.isColonVisible}
            isVisible
            onCancel={() => dismiss(banner.id)}
          />
        ))}
      </DSAlertBannerProvider>
    </AlertBannerContext.Provider>
  );
}

const NOOP_ALERT_BANNER: AlertBannerContextValue = {
  show: () => '',
  dismiss: () => {},
  dismissAll: () => {},
};

/**
 * Imperative API for showing/dismissing page-level stacking alert banners.
 * In production the app is wrapped in `AlertBannerProvider`; outside of one
 * (e.g. unit tests that render a single component) the hook returns no-op
 * implementations so consumers don't need to mock the provider.
 *
 * @example
 *   const { show } = useAlertBanner();
 *   show({ colorTheme: ALERT_BANNER_TYPES.SUCCESS, title: 'Saved.' });
 */
export function useAlertBanner(): AlertBannerContextValue {
  return useContext(AlertBannerContext) ?? NOOP_ALERT_BANNER;
}
